import { useState, useEffect, useCallback } from 'react';
import api from '../services/api';
import toastUtils from '../utils/toastUtils';
import { i18n } from '../translate/i18n';

export const useKanbanTasks = (ticketId = null) => {
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Carregar tarefas
  const fetchTasks = useCallback(async (filters = {}) => {
    setLoading(true);
    setError(null);
    
    try {
      const params = {
        ...filters,
        ...(ticketId && { ticketId }),
        withSubtasks: true,
        includeCompleted: true
      };

      const { data } = await api.get('/kanban-tasks', { params });
      setTasks(data.tasks || []);
      return data;
    } catch (err) {
      console.error('Error fetching tasks:', err);
      setError(err.message);
      toastUtils.error(i18n.t('kanban.messages.errorFetchingTasks'), 'Erro no Kanban');
      return { tasks: [], count: 0, hasMore: false };
    } finally {
      setLoading(false);
    }
  }, [ticketId]);

  // Criar nova tarefa
  const createTask = useCallback(async (taskData) => {
    setLoading(true);
    
    try {
      const { data } = await api.post('/kanban-tasks', taskData);
      
      setTasks(prevTasks => [...prevTasks, data]);
      toastUtils.success(i18n.t('kanban.messages.taskCreated'), 'Task Criada');
      
      return data;
    } catch (err) {
      console.error('Error creating task:', err);
      toastUtils.error(i18n.t('kanban.messages.errorCreatingTask'), 'Erro no Kanban');
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  // Atualizar tarefa
  const updateTask = useCallback(async (taskId, updateData) => {
    setLoading(true);
    
    try {
      const { data } = await api.put(`/kanban-tasks/${taskId}`, updateData);
      
      setTasks(prevTasks => 
        prevTasks.map(task => 
          task.id === parseInt(taskId) ? { ...task, ...data } : task
        )
      );
      
      toastUtils.success(i18n.t('kanban.messages.taskUpdated'), 'Status Alterado');
      return data;
    } catch (err) {
      console.error('Error updating task:', err);
      toastUtils.error(i18n.t('kanban.messages.errorUpdatingTask'), 'Erro no Kanban');
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  // Deletar tarefa
  const deleteTask = useCallback(async (taskId) => {
    setLoading(true);
    
    try {
      await api.delete(`/kanban-tasks/${taskId}`);
      
      setTasks(prevTasks => 
        prevTasks.filter(task => task.id !== parseInt(taskId))
      );
      
      toastUtils.success(i18n.t('kanban.messages.taskDeleted'), 'Status Alterado');
    } catch (err) {
      console.error('Error deleting task:', err);
      toastUtils.error(i18n.t('kanban.messages.errorDeletingTask'), 'Erro no Kanban');
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  // Mover tarefa (arrastar e soltar)
  const moveTask = useCallback(async (taskId, newStatus, sourceIndex, destinationIndex) => {
    try {
      // Atualização otimista da UI
      const taskToMove = tasks.find(t => t.id === parseInt(taskId));
      if (!taskToMove) return;

      const updatedTask = { ...taskToMove, status: newStatus, order: destinationIndex };
      
      setTasks(prevTasks => 
        prevTasks.map(task => 
          task.id === parseInt(taskId) ? updatedTask : task
        )
      );

      // Atualizar no backend
      await api.put(`/kanban-tasks/${taskId}/status`, { 
        status: newStatus,
        order: destinationIndex 
      });

      toastUtils.success(i18n.t('kanban.messages.taskMoved'), 'Card Movido');
    } catch (err) {
      console.error('Error moving task:', err);
      toastUtils.error(i18n.t('kanban.messages.errorMovingTask'), 'Erro no Kanban');
      // Reverter mudanças na UI
      fetchTasks();
    }
  }, [tasks, fetchTasks]);

  // Atualizar múltiplas tarefas (bulk update)
  const bulkUpdateTasks = useCallback(async (taskIds, updateData) => {
    setLoading(true);
    
    try {
      const { data } = await api.put('/kanban-tasks/bulk-update', {
        taskIds,
        updateData
      });

      setTasks(prevTasks =>
        prevTasks.map(task =>
          taskIds.includes(task.id) 
            ? { ...task, ...updateData }
            : task
        )
      );

      toastUtils.success(i18n.t('kanban.messages.bulkUpdateSuccess'), 'Status Alterado');
      return data;
    } catch (err) {
      console.error('Error bulk updating tasks:', err);
      toastUtils.error(i18n.t('kanban.messages.errorBulkUpdate'), 'Erro no Kanban');
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  // Carregar subtarefas
  const fetchSubtasks = useCallback(async (parentTaskId) => {
    try {
      const { data } = await api.get(`/kanban-tasks/${parentTaskId}/subtasks`);
      return data;
    } catch (err) {
      console.error('Error fetching subtasks:', err);
      toastUtils.error(i18n.t('kanban.messages.errorFetchingSubtasks'), 'Erro no Kanban');
      return [];
    }
  }, []);

  // Atualizar tempo trabalhado
  const updateTimeTracking = useCallback(async (taskId, actualHours) => {
    try {
      const { data } = await api.put(`/kanban-tasks/${taskId}/time-tracking`, { actualHours });
      
      setTasks(prevTasks =>
        prevTasks.map(task =>
          task.id === parseInt(taskId) 
            ? { ...task, actualHours: actualHours }
            : task
        )
      );

      toastUtils.success(i18n.t('kanban.messages.progressUpdated'), 'Status Alterado');
      return data;
    } catch (err) {
      console.error('Error updating time tracking:', err);
      toastUtils.error(i18n.t('kanban.messages.errorUpdatingProgress'), 'Erro no Kanban');
      throw err;
    }
  }, []);

  // Obter progresso do ticket
  const getTicketProgress = useCallback(async (ticketId) => {
    try {
      const { data } = await api.get(`/kanban/tickets/${ticketId}/progress`);
      return data;
    } catch (err) {
      console.error('Error fetching ticket progress:', err);
      return null;
    }
  }, []);

  // Carregar tarefas na inicialização
  useEffect(() => {
    fetchTasks();
  }, [fetchTasks]);

  return {
    tasks,
    loading,
    error,
    fetchTasks,
    createTask,
    updateTask,
    deleteTask,
    moveTask,
    bulkUpdateTasks,
    fetchSubtasks,
    updateTimeTracking,
    getTicketProgress
  };
};

export default useKanbanTasks;